import { Decimal } from 'decimal.js'; // Use a reliable decimal library

// --- Data Interfaces (Mirror JSON Structure) ---
export interface Item {
  id: number;
  name: string;
  code?: string | null;
  hsn_code?: string | null;
  description?: string | null;
  item_type: 'product' | 'service';
  is_active: boolean;
  organization_id: number;
  category_id?: number | null;
  base_unit_id: number;
  secondary_unit_id?: number | null;
  conversion_factor?: number | string | null; // Allow string for JSON parsing
  default_godown_id?: number | null;
  is_gst_applicable: boolean;
  tax_rate_id?: number | null;
  purchase_account_id?: number | null;
  sales_account_id?: number | null;
  inventory_account_id?: number | null;
  // Add other fields as needed
}

export interface ItemPrice {
  id: number;
  item_id: number;
  organization_id: number;
  price_type: 'sale' | 'purchase' | 'mrp' | 'cost';
  price: number | string; // Allow string for JSON parsing
  is_tax_inclusive: boolean;
  site_id?: number | null;
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface PriceList {
  id: number;
  name: string;
  organization_id: number;
  is_active: boolean;
  description?: string | null;
  applicable_to: 'customers' | 'vendors' | 'all';
  default_discount_type?: 'percentage' | 'fixed' | null;
  default_discount_value: number | string; // Allow string for JSON parsing
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface PriceListItem {
  id: number;
  price_list_id: number;
  item_id: number;
  organization_id: number;
  rate?: number | string | null; // Allow string
  discount_percent?: number | string | null; // Allow string
  discount_amount?: number | string | null; // Allow string
  min_qty: number | string; // Allow string
  max_qty?: number | string | null; // Allow string
  is_tax_inclusive: boolean;
}

// Tax Rate structure (Simplified - adapt if you have full tax data)
export interface TaxRate {
    id: number;
    name: string;
    code: string;
    rate: number | string; // Allow string
    components: TaxRateComponent[]; // Assumes structure with components
}
 
export interface TaxRateComponent {
    id: number;
    tax_component_id: number;
    rate: number | string; // Allow string
    // Simplified - assumes a TaxComponent lookup exists elsewhere
    tax_component: { id: number; code: string; name: string; };
}

// --- Util Function Return Type ---
export interface ItemPriceDetails {
  rate: Decimal;
  discount_percent: Decimal;
  discount_amount: Decimal;
  tax_rate_id: number | null;
  tax_amount: Decimal;
  tax_breakdown: Array<{ code: string; rate: Decimal; amount: Decimal }>; // CGST/SGST/IGST breakdown
  final_amount: Decimal; // Amount after tax
  taxable_amount: Decimal; // Amount before tax
}

// --- Util Function Input Type ---
export interface PricingContext {
    itemId: number;
    partyId?: number; // To determine applicable price list
    siteId?: number; // To determine site specific prices
    quantity: number | Decimal;
    // Optional: Pass all loaded data to avoid reloading inside the function
    itemsData?: Item[];
    itemPricesData?: ItemPrice[];
    priceListsData?: PriceList[];
    priceListItemsData?: PriceListItem[];
    taxRatesData?: TaxRate[]; // Add if tax data is available locally
}

// Import JSON data (ensure these paths are correct relative to this file)
import itemsDataJson from '@/app/data/items.json';
import itemPricesDataJson from '@/app/data/item_prices.json';
import priceListsDataJson from '@/app/data/price_lists.json';
import priceListItemsDataJson from '@/app/data/price_list_items.json';
import taxRatesDataJson from '@/app/data/tax_rates.json';
import taxRateComponentsDataJson from '@/app/data/tax_rate_components.json';

// Helper interfaces for tax data
interface TaxComponent {
  id: number;
  code: string;
  name: string;
}

interface TaxRateData {
  id: number;
  name: string;
  code: string;
  rate: number;
  description?: string;
  organization_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

interface TaxRateComponentData {
  id: number;
  tax_rate_id: number;
  tax_component_id: number;
  rate: number;
  organization_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

// Cast imported data to defined interfaces
const itemsData: Item[] = itemsDataJson as Item[];
const itemPricesData: ItemPrice[] = itemPricesDataJson as ItemPrice[];
const priceListsData: PriceList[] = priceListsDataJson as PriceList[];
const priceListItemsData: PriceListItem[] = priceListItemsDataJson as PriceListItem[];
const taxRatesData: TaxRateData[] = taxRatesDataJson as TaxRateData[];
const taxRateComponentsData: TaxRateComponentData[] = taxRateComponentsDataJson as TaxRateComponentData[];

// Define tax component codes for GST
const CGST_CODE = 'CGST';
const SGST_CODE = 'SGST';
const IGST_CODE = 'IGST';

// Map component IDs to their codes (assuming IDs 1 and 2 match CGST and SGST)
const taxComponentMap: Record<number, TaxComponent> = {
  1: { id: 1, code: CGST_CODE, name: 'Central GST' },
  2: { id: 2, code: SGST_CODE, name: 'State GST' },
  3: { id: 3, code: IGST_CODE, name: 'Integrated GST' }
};

/**
 * Calculates the price, discount, and tax for a given item based on context.
 */
export function getItemPriceDetails(context: PricingContext): ItemPriceDetails {
    const {
        itemId,
        siteId,
        quantity
    } = context;

    const qty = new Decimal(quantity);
    if (qty.isNaN() || qty.lte(0)) {
        console.warn(`[getItemPriceDetails] Invalid quantity: ${quantity}`);
        // Return zero/default values for invalid quantity
        return {
            rate: new Decimal(0),
            discount_percent: new Decimal(0),
            discount_amount: new Decimal(0),
            tax_rate_id: null,
            tax_amount: new Decimal(0),
            tax_breakdown: [],
            final_amount: new Decimal(0),
            taxable_amount: new Decimal(0)
        };
    }

    // 1. Find Item Data
    const item = itemsData.find(i => i.id === itemId);
    if (!item) {
        console.error(`[getItemPriceDetails] Item not found: ${itemId}`);
        throw new Error(`Item with ID ${itemId} not found.`);
    }

    // --- 2. Determine Applicable Price/Rate ---
    let baseRate = new Decimal(0);
    let resolvedDiscountPercent = new Decimal(0);
    let resolvedDiscountAmount = new Decimal(0);

    // Find customer's price list ID
    const customerPriceListId: number | null = null; // TODO: Determine this based on party data
    let priceListRuleFound = false;

    if (customerPriceListId) {
        const priceList = priceListsData.find(pl => pl.id === customerPriceListId && pl.is_active);
        if (priceList) {
            // Find item rule in the price list, considering quantity tiers
            const applicableRule = priceListItemsData.find(pli =>
                pli.price_list_id === customerPriceListId &&
                pli.item_id === itemId &&
                qty.gte(new Decimal(pli.min_qty)) &&
                (!pli.max_qty || qty.lte(new Decimal(pli.max_qty)))
            );

            if (applicableRule) {
                priceListRuleFound = true;
                if (applicableRule.rate !== null && applicableRule.rate !== undefined) {
                    baseRate = new Decimal(applicableRule.rate);
                    // Price list rate might override discounts
                    resolvedDiscountPercent = new Decimal(0);
                    resolvedDiscountAmount = new Decimal(0);
                } else if (applicableRule.discount_percent !== null && applicableRule.discount_percent !== undefined) {
                    resolvedDiscountPercent = new Decimal(applicableRule.discount_percent);
                    // Need base sale price to apply discount
                } else if (applicableRule.discount_amount !== null && applicableRule.discount_amount !== undefined) {
                    resolvedDiscountAmount = new Decimal(applicableRule.discount_amount);
                    // Need base sale price to apply discount
                }
                // TODO: Handle is_tax_inclusive for price list rate
            }

            // Apply Price List default discount if no specific item rule applied discount
            if (!priceListRuleFound || (resolvedDiscountPercent.isZero() && resolvedDiscountAmount.isZero())) {
                if (priceList.default_discount_type === 'percentage' && priceList.default_discount_value !== null) {
                    resolvedDiscountPercent = new Decimal(priceList.default_discount_value);
                } else if (priceList.default_discount_type === 'fixed' && priceList.default_discount_value !== null) {
                    // Fixed discount per unit? Needs clarification. Assuming per-unit for now.
                    console.warn("Fixed default discount on price list not fully implemented.");
                }
            }
        }
    }

    // Track if the price is tax inclusive
    let isTaxInclusive = false;
    
    // If no price list rate found, or if a discount needs a base rate, find base sale price
    if (baseRate.isZero() || !resolvedDiscountAmount.isZero() || !resolvedDiscountPercent.isZero()) {
        // Find base sale price from item_prices.json (consider site_id if applicable)
        const sitePrice = itemPricesData.find(p =>
            p.item_id === itemId &&
            p.price_type === 'sale' &&
            p.site_id === siteId // Check site-specific first
        );
        const generalPrice = itemPricesData.find(p =>
            p.item_id === itemId &&
            p.price_type === 'sale' &&
            !p.site_id // Fallback to general price
        );

        const baseSalePriceRecord = sitePrice || generalPrice;
        if (baseSalePriceRecord) {
            if (baseRate.isZero()) { // Only set baseRate if not already set by price list
                baseRate = new Decimal(baseSalePriceRecord.price);
                isTaxInclusive = baseSalePriceRecord.is_tax_inclusive || false;
            }
        } else {
            console.warn(`[getItemPriceDetails] No sale price found for item ${itemId}, using default price`);
            // Set a default price rather than leaving it at 0
            baseRate = new Decimal(50); // Default to 50 if no price found
            isTaxInclusive = false;
        }
    }

    // --- 3. Calculate Discount Amount ---
    let calculatedDiscountAmount = new Decimal(0);
    if (resolvedDiscountAmount.gt(0)) {
        calculatedDiscountAmount = resolvedDiscountAmount.mul(qty); // Assuming fixed discount is per unit
    } else if (resolvedDiscountPercent.gt(0)) {
        calculatedDiscountAmount = baseRate.mul(qty).mul(resolvedDiscountPercent).div(100);
    }

    // Ensure discount doesn't exceed gross amount
    const grossAmount = baseRate.mul(qty);
    calculatedDiscountAmount = Decimal.min(calculatedDiscountAmount, grossAmount);

    // Determine final discount percentage (if amount was used)
    const finalDiscountPercent = grossAmount.isZero()
        ? new Decimal(0)
        : calculatedDiscountAmount.div(grossAmount).mul(100);

    // --- 4. Calculate Taxable Amount ---
    let taxableAmount = grossAmount.sub(calculatedDiscountAmount);
    let finalAmount = taxableAmount; // Initialize final amount

    // --- 5. Calculate Tax ---
    let taxAmount = new Decimal(0);
    let taxBreakdown: Array<{ code: string; rate: Decimal; amount: Decimal }> = [];
    const taxRateId = item.is_gst_applicable ? (item.tax_rate_id || null) : null;

    console.log("[DEBUG] Tax calculation input:", {
        is_gst_applicable: item.is_gst_applicable,
        taxRateId,
        taxableAmount: taxableAmount.toString(),
        taxRatesCount: taxRatesData.length,
        taxComponentsCount: taxRateComponentsData.length
    });

    if (taxRateId) {
        // Find the tax rate from the tax_rates.json
        const taxRate = taxRatesData.find(tr => tr.id === taxRateId);
        
        console.log("[DEBUG] Found tax rate:", taxRate);
        console.log("[DEBUG] Is tax inclusive:", isTaxInclusive);
        
        if (taxRate) {
            // Get the overall tax rate percentage
            const totalTaxRate = new Decimal(taxRate.rate);
            
            if (isTaxInclusive) {
                // For tax-inclusive pricing, extract tax from the price
                // Formula: tax = (price * tax_rate) / (100 + tax_rate)
                taxAmount = taxableAmount.mul(totalTaxRate).div(new Decimal(100).add(totalTaxRate));
                // Adjust taxable amount to exclude tax
                taxableAmount = taxableAmount.sub(taxAmount);
                // Final amount remains the same (inclusive price)
                finalAmount = taxableAmount.add(taxAmount);
            } else {
                // For tax-exclusive pricing, add tax on top
                taxAmount = taxableAmount.mul(totalTaxRate).div(100);
                // Final amount is taxable amount plus tax
                finalAmount = taxableAmount.add(taxAmount);
            }
            
            // Get tax components for this tax rate
            const taxComponents = taxRateComponentsData.filter(tc => tc.tax_rate_id === taxRateId);
            
            console.log("[DEBUG] Tax components:", taxComponents);
            
            // If we have components, use them to calculate individual tax amounts
            if (taxComponents && taxComponents.length > 0) {
                taxBreakdown = taxComponents.map(component => {
                    // Get the component code
                    const componentId = component.tax_component_id;
                    const taxComponent = taxComponentMap[componentId] || 
                        { code: `Component-${componentId}`, id: componentId, name: `Tax Component ${componentId}` };
                    
                    // Calculate component rate and amount
                    const componentRate = new Decimal(component.rate);
                    let componentAmount: Decimal;
                    
                    if (isTaxInclusive) {
                        // For tax-inclusive, extract component tax from inclusive amount
                        const inclusiveBase = grossAmount.sub(calculatedDiscountAmount);
                        componentAmount = inclusiveBase.mul(componentRate).div(new Decimal(100).add(totalTaxRate)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
                    } else {
                        // For tax-exclusive, calculate normally
                        componentAmount = taxableAmount.mul(componentRate).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
                    }
                    
                    return {
                        code: taxComponent.code,
                        rate: componentRate,
                        amount: componentAmount
                    };
                });
                
                // Recalculate total tax as sum of components
                taxAmount = taxBreakdown.reduce((sum, comp) => sum.add(comp.amount), new Decimal(0));
            } else {
                // If no components found, use the total rate as a single component
                taxBreakdown = [
                    { code: 'TAX', rate: totalTaxRate, amount: taxAmount.toDecimalPlaces(2, Decimal.ROUND_HALF_UP) }
                ];
            }
        } else {
            console.warn(`[getItemPriceDetails] Tax Rate ID ${taxRateId} not found for item ${itemId}`);
            // If tax rate not found, use a default rate for fallback
            const defaultTaxRate = new Decimal(5); // 5% as fallback
            taxAmount = taxableAmount.mul(defaultTaxRate).div(100);
            
            // Default single tax component
            taxBreakdown = [
                { code: 'TAX', rate: defaultTaxRate, amount: taxAmount.toDecimalPlaces(2, Decimal.ROUND_HALF_UP) }
            ];
        }
    }

    // --- 6. Final Amount already calculated based on tax-inclusive/exclusive ---

    // --- 7. Return Detailed Price Info ---
    return {
        rate: baseRate.toDecimalPlaces(2),
        // Return the dominant discount type/value used
        discount_percent: resolvedDiscountAmount.gt(0) ? finalDiscountPercent.toDecimalPlaces(2) : resolvedDiscountPercent.toDecimalPlaces(2),
        discount_amount: calculatedDiscountAmount.toDecimalPlaces(2),
        tax_rate_id: taxRateId,
        tax_amount: taxAmount.toDecimalPlaces(2),
        tax_breakdown: taxBreakdown.map(b => ({ ...b, rate: b.rate.toDecimalPlaces(2), amount: b.amount.toDecimalPlaces(2) })),
        final_amount: finalAmount.toDecimalPlaces(2),
        taxable_amount: taxableAmount.toDecimalPlaces(2),
    };
}

/**
 * Simplified function for recalculating price details in the EditableDataGrid
 * This function can be used when quantity, rate, or discount changes
 */
export function recalculateItemPrice(
  itemId: number | undefined | null,
  taxRateId: number | undefined | null, 
  quantity: number | string,
  rate: number | string,
  discountPercent: number | string,
  discountAmount?: number | string,
  isTaxInclusive: boolean = false
): { 
  taxableAmount: number; 
  taxAmount: number; 
  totalAmount: number;
} {
  if (!itemId) {
    return { taxableAmount: 0, taxAmount: 0, totalAmount: 0 };
  }

  // Parse numeric values
  const qty = new Decimal(quantity || 0);
  const unitRate = new Decimal(rate || 0);
  const discPct = new Decimal(discountPercent || 0);
  const discAmt = new Decimal(discountAmount || 0);

  // Calculate gross amount
  const grossAmount = qty.mul(unitRate);
  
  // Calculate discount - use discount amount if provided, otherwise calculate from percentage
  let calculatedDiscountAmount: Decimal;
  if (discAmt.gt(0)) {
    calculatedDiscountAmount = discAmt;
  } else if (discPct.gt(0)) {
    calculatedDiscountAmount = grossAmount.mul(discPct).div(100);
  } else {
    calculatedDiscountAmount = new Decimal(0);
  }
  
  let taxableAmount = grossAmount.sub(calculatedDiscountAmount);
  let totalAmount = taxableAmount;
  let taxAmount = new Decimal(0);

  // If not taxable or no tax rate, return without tax
  if (!taxRateId) {
    return {
      taxableAmount: taxableAmount.toNumber(),
      taxAmount: 0,
      totalAmount: taxableAmount.toNumber()
    };
  }

  // Find tax rate
  const taxRate = taxRatesData.find(tr => tr.id === taxRateId);
  if (!taxRate) {
    return {
      taxableAmount: taxableAmount.toNumber(),
      taxAmount: 0,
      totalAmount: taxableAmount.toNumber()
    };
  }

  // Calculate tax based on the total rate 
  const totalTaxRate = new Decimal(taxRate.rate);
  
  if (isTaxInclusive) {
    // For tax-inclusive pricing, extract tax from the amount
    // Formula: tax = (amount * tax_rate) / (100 + tax_rate)
    taxAmount = taxableAmount.mul(totalTaxRate).div(new Decimal(100).add(totalTaxRate));
    // Adjust taxable amount to exclude tax
    taxableAmount = taxableAmount.sub(taxAmount);
    // Total amount remains the same (inclusive price)
    totalAmount = taxableAmount.add(taxAmount);
  } else {
    // For tax-exclusive pricing, add tax on top
    taxAmount = taxableAmount.mul(totalTaxRate).div(100);
    totalAmount = taxableAmount.add(taxAmount);
  }
  
  return {
    taxableAmount: taxableAmount.toDecimalPlaces(2).toNumber(),
    taxAmount: taxAmount.toDecimalPlaces(2).toNumber(),
    totalAmount: totalAmount.toDecimalPlaces(2).toNumber()
  };
} 